import AssignmentEditorHeader from 'components/CreateAssignment/AssignmentEditorHeader';
import QuestionSettingsStep from 'components/CreateQuiz/QuestionSettingsStep';
import QuizDetailsSettings from 'components/EditQuiz/QuizDetailsSettings';
import QuizFeedbackSettings from 'components/EditQuiz/QuizFeedbackSettings';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Icon, Stack, Tabs, Text, TextInput } from 'shell/components';
import { AUTOSAVE_STATUS } from 'utils/autosave';
import { ViewportDevice } from 'utils/constants';

const QUIZ_EDITOR_TAB = Object.freeze({
    DETAILS: 'details',
    FEEDBACK: 'feedback',
    QUESTIONS: 'questions',
});

const tabLabel = (label, hasError, errorLabel) =>
    hasError ? (
        <Box
            component={'span'}
            aria-label={`${label}, ${errorLabel}`}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: (theme) => theme.spacing(1.5),
            }}
        >
            <span>{label}</span>
            <Box
                component={'span'}
                aria-hidden={'true'}
                sx={{
                    flexShrink: 0,
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: (theme) => theme.palette.ensightful.error[600],
                }}
            />
        </Box>
    ) : (
        label
    );

const StandardPanel = ({ device, children }) => (
    <Box
        sx={(theme) => ({
            width: '100%',
            maxWidth: 900,
            mx: 'auto',
            px: device === ViewportDevice.MOBILE ? 4 : 8,
            py: 5,
            [theme.breakpoints.down('tablet')]: { px: 4 },
        })}
    >
        {children}
    </Box>
);

const EditQuizLayout = ({
    device,
    quiz,
    setQuiz,
    isQuizValid,
    autosaveStatus,
    lastSavedAt,
    tabErrors = {},
    onClickBack,
    onClickDelete,
    onClickSave,
}) => {
    const { t } = useTranslation(['quiz', 'assignmentSettings']);
    const tabErrorLabel = t('assignmentSettings:tabs.has-errors', 'Has errors');
    const [tab, setTab] = useState(QUIZ_EDITOR_TAB.DETAILS);
    const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
    const autosaveStatusContent =
        autosaveStatus === AUTOSAVE_STATUS.SAVING ? (
            <Stack direction={'row'} alignItems={'center'} spacing={2} aria-live={'polite'}>
                <Text color={'light'}>{t('assignmentSettings:autosave.saving', 'Saving...')}</Text>
            </Stack>
        ) : [AUTOSAVE_STATUS.PENDING, AUTOSAVE_STATUS.ERROR].includes(autosaveStatus) || !isQuizValid ? (
            <Button
                variant={'borderless'}
                onClick={onClickSave}
                aria-label={
                    autosaveStatus === AUTOSAVE_STATUS.ERROR
                        ? t('assignmentSettings:autosave.save-failed', 'Save failed. Save')
                        : t('assignmentSettings:autosave.save', 'Save')
                }
            >
                {t('assignmentSettings:autosave.save', 'Save')}
            </Button>
        ) : autosaveStatus === AUTOSAVE_STATUS.SAVED ? (
            <Stack direction={'row'} alignItems={'center'} spacing={1} aria-live={'polite'}>
                <Icon kind={'checkmark'} />
                <Text color={'light'}>{t('assignmentSettings:autosave.saved', 'Saved')}</Text>
            </Stack>
        ) : null;
    const tabs = [
        {
            key: QUIZ_EDITOR_TAB.DETAILS,
            value: tabLabel(
                t('quiz:edit.tabs.details', 'Details & Settings'),
                tabErrors[QUIZ_EDITOR_TAB.DETAILS],
                tabErrorLabel
            ),
            content: (
                <StandardPanel device={device}>
                    <QuizDetailsSettings device={device} />
                </StandardPanel>
            ),
        },
        {
            key: QUIZ_EDITOR_TAB.FEEDBACK,
            value: tabLabel(
                t('quiz:edit.tabs.feedback', 'Feedback Settings'),
                tabErrors[QUIZ_EDITOR_TAB.FEEDBACK],
                tabErrorLabel
            ),
            content: (
                <StandardPanel device={device}>
                    <QuizFeedbackSettings device={device} />
                </StandardPanel>
            ),
        },
        {
            key: QUIZ_EDITOR_TAB.QUESTIONS,
            value: tabLabel(
                t('quiz:edit.tabs.questions', 'Questions'),
                tabErrors[QUIZ_EDITOR_TAB.QUESTIONS],
                tabErrorLabel
            ),
            content: <QuestionSettingsStep appearance={'edit'} device={device} />,
        },
    ];

    return (
        <Container
            device={device}
            maxWidth={false}
            sx={(theme) => {
                const contentPadding = theme.spacing(device === ViewportDevice.MOBILE ? 4 : 8);

                return {
                    '--quiz-editor-content-padding': contentPadding,
                    '--assignment-editor-content-negative-padding': `-${contentPadding}`,
                    alignItems: 'flex-start',
                    minHeight: '100%',
                    maxWidth: 'none !important',
                    overflowX: 'clip',
                    px: 'var(--quiz-editor-content-padding)',
                    pb: 16,
                    background: theme.palette.ensightful.shades[0],
                };
            }}
        >
            <Stack spacing={4} sx={{ width: '100%', minWidth: 0 }}>
                <AssignmentEditorHeader
                    device={device}
                    busy={false}
                    isPersisted
                    autosaveStatusContent={autosaveStatusContent}
                    lastSavedAt={lastSavedAt}
                    onClickBack={onClickBack}
                    onClickCancel={onClickBack}
                    onClickDelete={onClickDelete}
                    onAction={onClickSave}
                    onHeightChange={setStickyHeaderHeight}
                />
                <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
                    <TextInput
                        inputProps={{ 'aria-label': t('quiz:edit.title-label', 'Quiz title') }}
                        placeholder={t('quiz:create.untitled', 'Untitled Quiz')}
                        value={quiz.title || ''}
                        onChange={(event) =>
                            setQuiz((current) => ({
                                ...current,
                                title: event.target.value,
                            }))
                        }
                        sx={(theme) => ({
                            width: '100%',
                            '&& > div > input': {
                                fontSize: '1.25rem',
                                fontWeight: theme.typography.sh1.fontWeight.semibold,
                                padding: theme.spacing(1, 3),
                            },
                            '&& > div > fieldset': { borderColor: 'transparent' },
                        })}
                    />
                </Box>
                <Tabs
                    aria-label={t('quiz:edit.tabs-label', 'Quiz settings')}
                    appearance={'underline'}
                    dragToScroll={device !== ViewportDevice.DESKTOP}
                    fullWidth
                    keepMounted
                    options={tabs}
                    selected={tab}
                    onChange={setTab}
                    sx={{
                        width: 'auto',
                        mx: 'var(--assignment-editor-content-negative-padding)',
                        '& > .MuiTabs-root': (theme) => ({
                            margin: 0,
                            position: 'sticky',
                            top: `calc(${stickyHeaderHeight}px + ${theme.spacing(5)} + ${theme.spacing(2)})`,
                            zIndex: 3,
                            background: theme.palette.ensightful.shades[0],
                            boxShadow: `0 -${theme.spacing(2)} 0 ${theme.palette.ensightful.shades[0]}`,
                            '& .MuiTab-root': {
                                minWidth: 150,
                            },
                        }),
                        '&& .MuiTabs-flexContainer': {
                            padding: '0 var(--quiz-editor-content-padding)',
                        },
                        '& > [role="tabpanel"]': { pt: 0 },
                    }}
                />
            </Stack>
        </Container>
    );
};

export default EditQuizLayout;
